import React, { useEffect, useMemo, useState } from 'react';

const MAX_WEEKS = 15;
const TZ = 'America/Chicago';

// Resolve backend base URL similarly to your other pages
const resolveBackendBase = () =>
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:5001'
    : 'https://pickem-backend-2025.onrender.com');

function isOk(res) { return res && res.ok; }
function normalizePlayerName(s) { return (s || '').trim(); }
const toLower = (s) => (s ?? '').toString().toLowerCase();

// Given a pick item (string or object), return { name, gameIndex|null }
function parsePickItem(x) {
  if (x == null) return { name: '', gameIndex: null };
  if (typeof x === 'string') return { name: x, gameIndex: null };
  if (typeof x === 'object') {
    return {
      name: x.pick || x.team || x.name || '',
      gameIndex: Number.isInteger(x.gameIndex) ? x.gameIndex : null,
    };
  }
  return { name: String(x), gameIndex: null };
}

// Find spread for a team given games array and (optional) gameIndex
function findSpreadForTeam(teamName, gameIndex, games) {
  if (!Array.isArray(games) || !teamName) return null;
  const lc = toLower(teamName);
  if (Number.isInteger(gameIndex) && games[gameIndex]) {
    const g = games[gameIndex];
    if (toLower(g.team1) === lc) return g.spread1 ?? null;
    if (toLower(g.team2) === lc) return g.spread2 ?? null;
  }
  // fallback: search by team name
  for (const g of games) {
    if (!g) continue;
    if (toLower(g.team1) === lc) return g.spread1 ?? null;
    if (toLower(g.team2) === lc) return g.spread2 ?? null;
  }
  return null;
}

// Format pick as "Team (±spread)" when available
function formatPickWithSpread(item, games) {
  const { name, gameIndex } = parsePickItem(item);
  const sp = findSpreadForTeam(name, gameIndex, games);
  if (sp === null || sp === undefined || sp === '') return name || '';
  const val = typeof sp === 'number' ? sp : parseFloat(sp);
  const pretty = Number.isFinite(val) ? (val > 0 ? `+${val}` : `${val}`) : `${sp}`;
  return `${name} (${pretty})`;
}

export default function AllPlayersPicksPage() {
  const backendBase = useMemo(resolveBackendBase, []);

  const [currentWeek, setCurrentWeek] = useState(null);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [picks, setPicks] = useState([]);
  const [winnersWeekly, setWinnersWeekly] = useState(null);
  const [totals, setTotals] = useState({});
  const [games, setGames] = useState([]);

  // Visibility flags from the **server** for the selected week
  const [revealFlag, setRevealFlag] = useState(false);
  const [cutoffISO, setCutoffISO] = useState(null);

  // 1) Load current week from server
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${backendBase}/data/current_week.json`, { cache: 'no-store' });
        const j = await r.json();
        if (!alive) return;
        const cw = j?.currentWeek ?? j?.week ?? 1;
        setCurrentWeek(cw);
      } catch {
        if (!alive) return; setCurrentWeek(1);
      }
    })();
    return () => { alive = false; };
  }, [backendBase]);

  // 2) Determine available weeks by checking for picks_week_X.json (limit to 1..currentWeek)
  useEffect(() => {
    if (currentWeek == null) return;
    let alive = true;
    (async () => {
      const found = [];
      const upper = Math.max(1, Math.min(MAX_WEEKS, currentWeek));
      for (let w = 1; w <= upper; w++) {
        try {
          const r = await fetch(`${backendBase}/data/picks_week_${w}.json`, { method: 'HEAD', cache: 'no-store' });
          if (isOk(r)) found.push(w);
        } catch {}
      }
      if (!alive) return;
      setAvailableWeeks(found.length ? found : [currentWeek]);

      // Default selection: prefer current week if it exists, otherwise the last available
      setSelectedWeek(prev => {
        if (prev) return prev;
        if (found.includes(currentWeek)) return currentWeek;
        return found[found.length - 1] || currentWeek || 1;
      });
    })();
    return () => { alive = false; };
  }, [backendBase, currentWeek]);

  // 3) Ask the **server** whether picks should be revealed for the selected week
  useEffect(() => {
    if (!selectedWeek) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${backendBase}/api/lock-status?week=${selectedWeek}`, { cache: 'no-store' });
        const j = await r.json();
        if (!alive) return;
        setRevealFlag(Boolean(j?.revealPicks));
        setCutoffISO(j?.cutoffISO || null);
      } catch {
        if (!alive) return;
        setRevealFlag(false);
        setCutoffISO(null);
      }
    })();
    return () => { alive = false; };
  }, [backendBase, selectedWeek]);

  // 4) Load data for selected week (auto-refresh also when reveal flips)
  useEffect(() => {
    if (!selectedWeek) return;
    let alive = true;
    setLoading(true); setError('');
    setPicks([]); setWinnersWeekly(null); setGames([]);

    (async () => {
      try {
        // picks (may be blocked by server if not revealed yet)
        const p = await fetch(`${backendBase}/data/picks_week_${selectedWeek}.json`, { cache: 'no-store' });
        let picksJson = p.ok ? await p.json() : [];

        // Fallback: if current week is gated or empty, build names-only rows from stats
        if ((!Array.isArray(picksJson) || picksJson.length === 0) &&
            currentWeek != null && selectedWeek === currentWeek && !revealFlag) {
          try {
            const s = await fetch(`${backendBase}/api/stats/picks?week=${selectedWeek}`, { cache: 'no-store' });
            if (s.ok) {
              const sj = await s.json();
              const names = Array.isArray(sj?.submittedNames) ? sj.submittedNames
                         : Array.isArray(sj?.names) ? sj.names
                         : [];
              if (names.length) {
                picksJson = names.map(n => ({ name: n, picks: [] }));
              }
            }
          } catch {}
        }

        // winners (per-player)
        let winnersWeeklyJson = null;
        try {
          const w = await fetch(`${backendBase}/data/winners_week_${selectedWeek}.json`, { cache: 'no-store' });
          if (w.ok) winnersWeeklyJson = await w.json();
        } catch {}

        // games (for spreads)
        try {
          const g = await fetch(`${backendBase}/data/games_week_${selectedWeek}.json`, { cache: 'no-store' });
          if (g.ok) setGames(await g.json());
        } catch {}

        // totals
        let totalsJson = {};
        try {
          const t = await fetch(`${backendBase}/data/totals.json`, { cache: 'no-store' });
          if (t.ok) totalsJson = await t.json();
        } catch {}

        if (!alive) return;
        setPicks(Array.isArray(picksJson) ? picksJson : []);
        setWinnersWeekly(winnersWeeklyJson);
        setTotals(totalsJson && typeof totalsJson === 'object' ? totalsJson : {});
        setLoading(false);
      } catch (e) {
        if (!alive) return;
        setLoading(false);
        setError('No data available for this week yet.');
      }
    })();

    return () => { alive = false; };
  }, [backendBase, selectedWeek, revealFlag, currentWeek]);

  // 5) Winners map
  const winnersMap = React.useMemo(() => {
    const map = new Map();
    if (!winnersWeekly) return map;

    if (Array.isArray(winnersWeekly)) {
      for (const row of winnersWeekly) {
        const name = normalizePlayerName(row.player || row.name || row.gameName);
        if (!name) continue;
        const corrArr = Array.isArray(row.correctPicks || row.correct) ? (row.correctPicks || row.correct) : [];
        map.set(name, {
          weeklyPoints: row.weeklyPoints ?? row.points ?? row.total ?? (corrArr ? corrArr.length : 0) ?? 0,
          correctCount: Array.isArray(corrArr) ? corrArr.length : (row.weeklyPoints ?? row.points ?? row.total ?? 0)
        });
      }
    } else if (winnersWeekly && typeof winnersWeekly === 'object') {
      for (const [nameRaw, val] of Object.entries(winnersWeekly)) {
        const name = normalizePlayerName(nameRaw);
        const corrArr = Array.isArray(val?.correctPicks || val?.correct) ? (val.correctPicks || val.correct) : [];
        map.set(name, {
          weeklyPoints: val?.weeklyPoints ?? val?.points ?? val?.total ?? (corrArr ? corrArr.length : 0) ?? 0,
          correctCount: Array.isArray(corrArr) ? corrArr.length : (val?.weeklyPoints ?? val?.points ?? val?.total ?? 0)
        });
      }
    }
    return map;
  }, [winnersWeekly]);

  const cumulative = React.useMemo(() => totals || {}, [totals]);

  const isHistorical = selectedWeek != null && currentWeek != null && selectedWeek < currentWeek;
  const isCurrentWeek = selectedWeek != null && currentWeek != null && selectedWeek === currentWeek;
  const canShowPicks = isHistorical || (isCurrentWeek && revealFlag);

  return (
    <div className="page-container">
      <h2>All Players' Picks</h2>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <label htmlFor="weekSel">Select Week:</label>
        <select
          id="weekSel"
          value={selectedWeek || ''}
          onChange={(e) => setSelectedWeek(Number(e.target.value))}
        >
          {(availableWeeks.length ? availableWeeks : (currentWeek ? Array.from({ length: currentWeek }, (_, i) => i + 1) : [1]))
            .map((w) => (
              <option key={w} value={w}>
                {`Week ${w}${currentWeek === w ? ' (current)' : ''}`}
              </option>
            ))}
        </select>
        {isCurrentWeek && !revealFlag && (
          <span style={{ color: '#666', fontSize: 12 }}>
            Picks unlock at the server’s cutoff
            {cutoffISO ? ` (${new Date(cutoffISO).toLocaleString('en-US', { timeZone: TZ })})` : ''}.
          </span>
        )}
      </div>

      {loading && <p>Loading…</p>}
      {!loading && error && <p style={{ color: 'gray' }}>{error}</p>}

      {!loading && !error && (
        <div style={{ overflowX: 'auto' }}>
          {(() => {
            const cell = { padding: '8px 10px', border: '1px solid #ddd', verticalAlign: 'top' };
            const pill = { background: '#fafafa', border: '1px solid #eee', borderRadius: 10, padding: '2px 8px', display: 'inline-block' };
            return (
              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd' }}>
                <thead>
                  <tr>
                    <th style={{ ...cell, background: '#f9f9f9', textAlign: 'left' }}>Game Name</th>
                    <th style={{ ...cell, background: '#f9f9f9', textAlign: 'left' }}>Picks (with spread)</th>
                    <th style={{ ...cell, background: '#f9f9f9', textAlign: 'left' }}>Winnings</th>
                    <th style={{ ...cell, background: '#f9f9f9', textAlign: 'left' }}>Weekly Points</th>
                    <th style={{ ...cell, background: '#f9f9f9', textAlign: 'left' }}>Cumulative</th>
                  </tr>
                </thead>
                <tbody>
                  {picks.map((p, idx) => {
                    const name = normalizePlayerName(p.gameName || p.player || p.name);
                    const winners = winnersMap.get(name);
                    const weeklyPts = winners?.weeklyPoints ?? null;
                    const correctCount = winners?.correctCount ?? (Number.isFinite(weeklyPts) ? weeklyPts : null);

                    const rawPicks = Array.isArray(p.picks) ? p.picks : [];
                    const picksList = rawPicks.map(item => formatPickWithSpread(item, games)).filter(Boolean);
                    const picksCount = 10; // locked to 10, per rules

                    const lowerTotals = new Map(Object.entries(cumulative || {}).map(([k, v]) => [normalizePlayerName(k).toLowerCase(), v]));
                    const cum = lowerTotals.has(name.toLowerCase()) ? lowerTotals.get(name.toLowerCase()) : 0;

                    return (
                      <tr key={`${name}-${idx}`}>
                        <td style={cell}>{name}</td>

                        <td style={cell}>
                          {canShowPicks ? (
                            picksList.length ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {picksList.map((t, i2) => (
                                  <span key={i2} style={pill}>{t}</span>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: '#999' }}>No picks recorded</span>
                            )
                          ) : (
                            <span style={{ color: '#999' }}>Hidden until cutoff</span>
                          )}
                        </td>

                        <td style={cell}>
                          {isCurrentWeek && !revealFlag ? (
                            <span style={{ color: '#999' }}>—/10</span>
                          ) : (
                            Number.isFinite(correctCount) ? `${correctCount} / ${picksCount}` : '—/10'
                          )}
                        </td>

                        <td style={cell}>
                          {isCurrentWeek && !revealFlag ? (
                            <span style={{ color: '#999' }}>—</span>
                          ) : (
                            Number.isFinite(weeklyPts) ? weeklyPts : 'N/A'
                          )}
                        </td>

                        <td style={cell}>{cum}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()}
        </div>
      )}
    </div>
  );
}
